import { useEffect, useRef, useState } from 'react';
import { useChat } from '@ai-sdk/react';
import { useMutation, useQuery } from '@/lib/api-hooks';
import { Send } from 'lucide-react';
import { backendApi } from '@/lib/backend';
import { relativeTime } from '@/lib/relative-time';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Loader } from '@/components/ui/Loader';
import { ChatMarkdown } from '@/components/chat/ChatMarkdown';
import type { Id } from '../../../convex/_generated/dataModel';
import type { Message } from 'ai';

interface ChatLayoutProps {
  customerId: string;
  customerName: string;
}

interface ChatSessionRow {
  id: string;
  title?: string;
  updatedAt?: string;
}

function newChatInstanceId(customerId: string) {
  return `new-${customerId}-${Date.now()}`;
}

function parseStoredMessages(raw: string | undefined): Message[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Message[]) : [];
  } catch {
    return [];
  }
}

export function ChatLayout({ customerId, customerName }: ChatLayoutProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<Message[]>([]);
  /** Persisted database session id (for save/sidebar). Independent of useChat instance id. */
  const sessionIdRef = useRef<string | undefined>(undefined);
  /** When true, load messages from PostgreSQL once the session query resolves. */
  const shouldLoadFromDbRef = useRef(false);

  const [activeSessionId, setActiveSessionId] = useState<string | undefined>();
  // Stable for a conversation. Changing this remounts useChat — only change on new/open.
  const [chatInstanceId, setChatInstanceId] = useState(() => newChatInstanceId(customerId));
  const [loadError, setLoadError] = useState<string | null>(null);
  const [chatError, setChatError] = useState<string | null>(null);

  const chatContext = useQuery(backendApi.chatSessions.getChatContext, {
    customerId: customerId as Id<'customers'>,
  }) as
    | { mode: 'technician' | 'customer'; role: string; customerName: string }
    | undefined;

  const sessions = useQuery(backendApi.chatSessions.list, {
    customerId: customerId as Id<'customers'>,
  }) as ChatSessionRow[] | undefined;
  const saveSession = useMutation(backendApi.chatSessions.save);
  const activeSession = useQuery(
    backendApi.chatSessions.get,
    activeSessionId
      ? { sessionId: activeSessionId as Id<'chatSession'> }
      : 'skip'
  ) as
    | {
        id: string;
        messages: string;
        title?: string;
      }
    | null
    | undefined;

  const {
    messages,
    input,
    setInput,
    status,
    setMessages,
    append,
    error,
    stop,
  } = useChat({
    api: `/api/chat?customerId=${encodeURIComponent(customerId)}`,
    id: chatInstanceId,
    maxSteps: 15,
    fetch: async (url, options) => {
      const headers = new Headers(options?.headers);
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });
      if (!response.ok) {
        let detail = `Chat request failed (${response.status})`;
        try {
          const body = await response.clone().json();
          if (body?.error) detail = String(body.error);
        } catch {
          try {
            const text = await response.clone().text();
            if (text) detail = text.slice(0, 300);
          } catch {
            // ignore
          }
        }
        throw new Error(detail);
      }
      return response;
    },
    onError: (err) => {
      console.error('Chat error:', err);
      setChatError(err?.message || 'Chat failed');
    },
    onFinish: async (message) => {
      setChatError(null);
      try {
        const prior = messagesRef.current.filter((m) => m.id !== message.id);
        const finalMessages = [...prior, message];
        messagesRef.current = finalMessages;
        const title =
          finalMessages
            .find((m) => m.role === 'user')
            ?.content?.toString()
            .slice(0, 80) || 'Chat';
        const result = (await saveSession({
          sessionId: sessionIdRef.current
            ? (sessionIdRef.current as Id<'chatSession'>)
            : undefined,
          customerId: customerId as Id<'customers'>,
          title,
          messages: JSON.stringify(finalMessages),
        })) as { sessionId: string };

        // Keep useChat instance id stable — only track session for persistence/sidebar.
        if (result?.sessionId) {
          sessionIdRef.current = result.sessionId;
          shouldLoadFromDbRef.current = false;
          setActiveSessionId(result.sessionId);
        }
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Failed to save chat');
      }
    },
  });

  messagesRef.current = messages;
  const thinking = status === 'streaming' || status === 'submitted';
  const displayError = chatError || (error ? error.message : null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  useEffect(() => {
    sessionIdRef.current = undefined;
    shouldLoadFromDbRef.current = false;
    setActiveSessionId(undefined);
    setChatInstanceId(newChatInstanceId(customerId));
    setMessages([]);
    setLoadError(null);
    setChatError(null);
  }, [customerId, setMessages]);

  useEffect(() => {
    if (!activeSessionId || activeSession === undefined) return;
    if (!shouldLoadFromDbRef.current) return;

    if (activeSession === null) {
      setLoadError('Session not found');
      shouldLoadFromDbRef.current = false;
      return;
    }
    const loaded = parseStoredMessages(activeSession.messages);
    setMessages(loaded);
    messagesRef.current = loaded;
    setLoadError(null);
    setChatError(null);
    shouldLoadFromDbRef.current = false;
  }, [activeSession, activeSessionId, setMessages]);

  async function startNewChat() {
    stop?.();
    sessionIdRef.current = undefined;
    shouldLoadFromDbRef.current = false;
    setActiveSessionId(undefined);
    setChatInstanceId(newChatInstanceId(customerId));
    setMessages([]);
    setLoadError(null);
    setChatError(null);
  }

  function openSession(sessionId: string) {
    stop?.();
    sessionIdRef.current = sessionId;
    shouldLoadFromDbRef.current = true;
    setActiveSessionId(sessionId);
    setChatInstanceId(sessionId);
    setLoadError(null);
    setChatError(null);
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4 min-h-[70vh]">
      <Card animate={false} className="h-fit lg:sticky lg:top-4">
        <Button type="button" className="w-full mb-3" onClick={() => void startNewChat()}>
          + New chat
        </Button>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
          Sessions
        </p>
        <div className="space-y-1 max-h-80 overflow-y-auto">
          {(sessions ?? []).map((session) => {
            const isActive = session.id === activeSessionId;
            return (
              <button
                key={session.id}
                type="button"
                onClick={() => openSession(session.id)}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
              >
                <div className="font-medium truncate">{session.title || 'Chat'}</div>
                {session.updatedAt && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {relativeTime(new Date(session.updatedAt))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </Card>

      <Card animate={false} className="flex flex-col min-h-[70vh]">
        <div className="border-b border-gray-200 dark:border-gray-700 pb-3 mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {chatContext?.mode === 'technician'
              ? `Technician mode — provision ${customerName}'s PBX and ask about call stats`
              : `Ask about ${customerName}'s calls, missed-call reports, and how the phone system is set up`}
          </p>
          {chatContext?.mode && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
              {chatContext.mode === 'technician'
                ? 'Full provisioning tools enabled for staff.'
                : 'Customer mode: analytics and read-only configuration (no PBX changes).'}
            </p>
          )}
        </div>

        {(loadError || displayError) && (
          <div className="mb-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {loadError || displayError}
          </div>
        )}

        <div className="flex-1 overflow-y-auto space-y-4 mb-4">
          {messages.length === 0 && !thinking && (
            <div className="flex flex-col items-center justify-center py-16 text-center text-gray-500 dark:text-gray-400">
              <div className="text-3xl mb-2">✦</div>
              <p className="font-semibold text-gray-900 dark:text-white">
                Ask the assistant to do something
              </p>
              <p className="text-sm mt-1">
                Try &ldquo;List all extensions and show which ones are unregistered&rdquo;
              </p>
            </div>
          )}
          {messages.map((message) => {
            let content = '';
            if (typeof message.content === 'string') {
              content = message.content;
            } else if (Array.isArray(message.content as unknown[])) {
              content = (message.content as unknown[])
                .map((part) => {
                  if (typeof part === 'string') return part;
                  if (part && typeof part === 'object' && 'text' in part) {
                    return String((part as { text?: unknown }).text ?? '');
                  }
                  return '';
                })
                .join('');
            }
            if (!content && message.role !== 'user') return null;
            const isUser = message.role === 'user';
            return (
              <div
                key={message.id}
                className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[min(100%,42rem)] rounded-2xl px-4 py-3 shadow-sm ${
                    isUser
                      ? 'bg-primary-600 text-white'
                      : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100'
                  }`}
                >
                  {isUser ? (
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{content}</p>
                  ) : (
                    <ChatMarkdown content={content} variant="assistant" />
                  )}
                </div>
              </div>
            );
          })}
          {thinking && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Loader size="sm" />
              Thinking…
              <button
                type="button"
                className="ml-2 text-xs underline hover:text-gray-700 dark:hover:text-gray-200"
                onClick={() => stop?.()}
              >
                Stop
              </button>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!input.trim() || thinking) return;
            setChatError(null);
            void append({ role: 'user', content: input.trim() });
            setInput('');
          }}
          className="flex gap-2 border-t border-gray-200 dark:border-gray-700 pt-4"
        >
          <Input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="Ask about extensions, trunks, routes…"
            disabled={thinking}
          />
          <Button type="submit" disabled={thinking || !input.trim()} className="shrink-0">
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </Card>
    </div>
  );
}
